import React from 'react';
import { cn } from '@/lib/utils';

/*
 * Dense tables on phones: never let a table stretch the page sideways. `TableScroll` gives the
 * table its own horizontal scroller (with edge fades from .table-scroll in index.css) and a
 * sensible minimum width so columns stop collapsing into one-word-per-line cells.
 *
 * Use it as:  <TableScroll><Table>...</Table></TableScroll>
 * For lists where a card per row reads better on a phone, render `mobile` (shown < md) and keep
 * the table for md+:  <TableScroll mobile={<CardList .../>}>...</TableScroll>
 */
export function TableScroll({ children, mobile, minWidth, className }) {
  return (
    <>
      {mobile && <div className="md:hidden space-y-2">{mobile}</div>}
      <div
        className={cn('table-scroll rounded-lg', mobile && 'hidden md:block', className)}
        style={minWidth ? { '--table-min': minWidth } : undefined}
      >
        {children}
      </div>
    </>
  );
}

/* A stat tile that works from 320px up: label above, number below, optional delta/sub line. */
export function StatTile({ label, value, sub, icon: Icon, tone = 'primary', onClick, className, testId }) {
  const tones = {
    primary: 'text-primary bg-primary/10 ring-primary/20',
    success: 'text-success bg-success/10 ring-success/20',
    warning: 'text-warning bg-warning/10 ring-warning/20',
    danger: 'text-danger bg-danger/10 ring-danger/20',
    info: 'text-info bg-info/10 ring-info/20',
    muted: 'text-muted-foreground bg-muted/40 ring-border',
  };
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'surface p-4 flex items-center justify-between gap-3 text-left w-full transition-colors',
        onClick && 'hover:border-primary/40',
        className
      )}
    >
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">{label}</p>
        <p className="mt-1 text-2xl font-semibold tnum text-foreground leading-none">{value}</p>
        {sub && <p className="mt-1.5 text-xs text-muted-foreground truncate">{sub}</p>}
      </div>
      {Icon && (
        <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1', tones[tone] || tones.primary)}>
          <Icon className="h-5 w-5" />
        </span>
      )}
    </Comp>
  );
}

/* 1 → 2 → 4 columns as the viewport grows; the default for a row of stat tiles. */
export function StatGrid({ children, className, cols = 4 }) {
  const map = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 xl:grid-cols-4', 5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5', 6: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6' };
  return <div className={cn('grid grid-cols-1 gap-3 sm:gap-4', map[cols] || map[4], className)}>{children}</div>;
}
